import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ProgressContext = createContext(null);

const STORAGE_KEY = 'mquery_progress';

const XP_PER_LEVEL = 1000;
const BASE_XP = 100;

// Achievement definitions
const ACHIEVEMENTS = [
  { id: 'first_steps', name: 'First Steps', description: 'Complete your first challenge', check: (p) => p.challenges_completed >= 1 },
  { id: 'quick_learner', name: 'Quick Learner', description: 'Complete 10 challenges', check: (p) => p.challenges_completed >= 10 },
  { id: 'perfectionist', name: 'Perfectionist', description: 'Get 3 stars on 10 challenges', check: (p) => p.perfect_challenges >= 10 },
  { id: 'dedicated', name: 'Dedicated', description: 'Complete 50 challenges', check: (p) => p.challenges_completed >= 50 },
  { id: 'xp_collector', name: 'XP Collector', description: 'Earn 5000 XP', check: (p) => p.total_xp >= 5000 },
  { id: 'rising_star', name: 'Rising Star', description: 'Reach level 5', check: (p) => p.level >= 5 },
  { id: 'm_expert', name: 'M Expert', description: 'Reach level 10', check: (p) => p.level >= 10 },
  { id: 'm_mentor', name: 'M Mentor', description: 'Reach level 15', check: (p) => p.level >= 15 },
  { id: 'm_grandmaster', name: 'M Grandmaster', description: 'Reach level 20', check: (p) => p.level >= 20 },
  { id: 'basics_master', name: 'Basics Master', description: 'Complete all Basics challenges', check: (p) => p.worlds?.['01_basics']?.completion === 100 },
  { id: 'types_master', name: 'Types Master', description: 'Complete all Types challenges', check: (p) => p.worlds?.['02_types']?.completion === 100 },
  { id: 'lists_master', name: 'Lists Master', description: 'Complete all Lists challenges', check: (p) => p.worlds?.['03_lists']?.completion === 100 },
  { id: 'records_master', name: 'Records Master', description: 'Complete all Records challenges', check: (p) => p.worlds?.['04_records']?.completion === 100 },
  { id: 'tables_master', name: 'Tables Master', description: 'Complete all Tables challenges', check: (p) => p.worlds?.['05_tables']?.completion === 100 },
  { id: 'functions_master', name: 'Functions Master', description: 'Complete all Functions challenges', check: (p) => p.worlds?.['06_functions']?.completion === 100 },
  { id: 'advanced_master', name: 'Advanced Master', description: 'Complete all Advanced challenges', check: (p) => p.worlds?.['07_advanced']?.completion === 100 },
  { id: 'formats_master', name: 'Formats Master', description: 'Complete all Formats challenges', check: (p) => p.worlds?.['08_formats']?.completion === 100 },
  { id: 'challenge_master', name: 'Challenge Master', description: 'Complete all Challenge challenges', check: (p) => p.worlds?.['09_challenge']?.completion === 100 },
  { id: 'completionist', name: 'Completionist', description: 'Complete all challenges', check: (p) => {
    const topicIds = ['01_basics', '02_types', '03_lists', '04_records', '05_tables', '06_functions', '07_advanced', '08_formats', '09_challenge'];
    return topicIds.every(id => p.worlds?.[id]?.completion === 100);
  }},
];

function createInitialProgress() {
  return {
    level: 1,
    total_xp: 0,
    current_xp: 0,
    challenges_completed: 0,
    perfect_challenges: 0,
    achievements: [],
    worlds: {},
    user_code: {},
    seen_intros: [],
    created: new Date().toISOString(),
    last_saved: new Date().toISOString(),
  };
}

const MAX_LEVEL = 20;

function calculateLevel(totalXp) {
  const level = Math.floor(totalXp / XP_PER_LEVEL) + 1;
  return Math.min(level, MAX_LEVEL); // Cap at max level
}

function calculateCurrentXp(totalXp) {
  const level = Math.floor(totalXp / XP_PER_LEVEL) + 1;
  if (level >= MAX_LEVEL) {
    return XP_PER_LEVEL; // Full bar at max level
  }
  return totalXp % XP_PER_LEVEL;
}

export function ProgressProvider({ children }) {
  const [progress, setProgress] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load progress from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setProgress(JSON.parse(saved));
      } else {
        setProgress(createInitialProgress());
      }
    } catch (err) {
      console.error('Failed to load progress:', err);
      setProgress(createInitialProgress());
    }
    setIsLoaded(true);
  }, []);

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    if (progress && isLoaded) {
      try {
        const toSave = {
          ...progress,
          last_saved: new Date().toISOString(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      } catch (err) {
        console.error('Failed to save progress:', err);
      }
    }
  }, [progress, isLoaded]);

  const completeChallenge = useCallback((worldId, challengeId, stars, totalChallengesInWorld) => {
    setProgress(prev => {
      // Deep copy to ensure React detects changes
      const prevWorld = prev.worlds[worldId] || {
        completion: 0,
        completed: [],
        stars: {},
      };

      const wasCompleted = prevWorld.completed.includes(challengeId);
      const previousStars = prevWorld.stars[challengeId] || 0;

      // Build new completed array
      const newCompleted = wasCompleted
        ? prevWorld.completed
        : [...prevWorld.completed, challengeId];

      // Build new stars object
      const newStars = stars > previousStars
        ? { ...prevWorld.stars, [challengeId]: stars }
        : prevWorld.stars;

      // Calculate new topic completion
      const newCompletion = Math.round((newCompleted.length / totalChallengesInWorld) * 100);

      // Build new topic object
      const newWorld = {
        completion: newCompletion,
        completed: newCompleted,
        stars: newStars,
      };

      // Calculate XP and other updates
      let newChallengesCompleted = prev.challenges_completed;
      let newPerfectChallenges = prev.perfect_challenges;

      if (!wasCompleted) {
        newChallengesCompleted += 1;
      }

      if (stars > previousStars && stars === 3 && previousStars < 3) {
        newPerfectChallenges += 1;
      }

      // Build new progress object with all new references
      const newProgress = {
        ...prev,
        challenges_completed: newChallengesCompleted,
        perfect_challenges: newPerfectChallenges,
        worlds: {
          ...prev.worlds,
          [worldId]: newWorld,
        },
      };

      // Award XP (only for first completion or star improvement)
      let xpGained = 0;
      if (!wasCompleted) {
        xpGained = BASE_XP + (stars - 1) * 25;
      } else if (stars > previousStars) {
        xpGained = (stars - previousStars) * 25;
      }

      if (xpGained > 0) {
        newProgress.total_xp = prev.total_xp + xpGained;
        newProgress.level = calculateLevel(newProgress.total_xp);
        newProgress.current_xp = calculateCurrentXp(newProgress.total_xp);
      }

      // Check achievements
      const earnedAchievements = ACHIEVEMENTS
        .filter(a => !newProgress.achievements.includes(a.name) && a.check(newProgress))
        .map(a => a.name);

      if (earnedAchievements.length > 0) {
        newProgress.achievements = [...newProgress.achievements, ...earnedAchievements];
      }

      return newProgress;
    });
  }, []);

  const saveUserCode = useCallback((worldId, challengeId, code) => {
    setProgress(prev => ({
      ...prev,
      user_code: {
        ...prev.user_code,
        [`${worldId}:${challengeId}`]: code,
      },
    }));
  }, []);

  const getUserCode = useCallback((worldId, challengeId) => {
    return progress?.user_code?.[`${worldId}:${challengeId}`] || '';
  }, [progress]);

  const markIntroSeen = useCallback((worldId) => {
    setProgress(prev => {
      if (prev.seen_intros.includes(worldId)) return prev;
      return {
        ...prev,
        seen_intros: [...prev.seen_intros, worldId],
      };
    });
  }, []);

  const hasSeenIntro = useCallback((worldId) => {
    return progress?.seen_intros?.includes(worldId) || false;
  }, [progress]);

  const getTopicProgress = useCallback((worldId) => {
    return progress?.worlds?.[worldId] || { completion: 0, completed: [], stars: {} };
  }, [progress]);

  const getChallengeStars = useCallback((worldId, challengeId) => {
    return progress?.worlds?.[worldId]?.stars?.[challengeId] || 0;
  }, [progress]);

  const isChallengeCompleted = useCallback((worldId, challengeId) => {
    return progress?.worlds?.[worldId]?.completed?.includes(challengeId) || false;
  }, [progress]);

  const resetProgress = useCallback(() => {
    setProgress(createInitialProgress());
  }, []);

  // Sync progress with current challenge data (recalculate completions when challenges change)
  const syncWithChallenges = useCallback((challengeData) => {
    if (!challengeData || !progress) return;

    setProgress(prev => {
      let needsUpdate = false;
      const newWorlds = { ...prev.worlds };

      // Recalculate completion for each topic
      for (const [topicId, challenges] of Object.entries(challengeData)) {
        const totalChallenges = challenges.length;
        const worldProgress = prev.worlds[topicId];

        if (worldProgress) {
          // Filter completed to only include challenges that still exist
          const validChallengeIds = new Set(challenges.map(c => c.id));
          const validCompleted = worldProgress.completed.filter(id => validChallengeIds.has(id));

          // Recalculate completion percentage
          const newCompletion = Math.round((validCompleted.length / totalChallenges) * 100);

          // Check if anything changed
          if (newCompletion !== worldProgress.completion || validCompleted.length !== worldProgress.completed.length) {
            needsUpdate = true;
            newWorlds[topicId] = {
              ...worldProgress,
              completed: validCompleted,
              completion: newCompletion,
            };
          }
        }
      }

      if (!needsUpdate) return prev;

      // Recalculate total challenges completed
      let totalCompleted = 0;
      let totalPerfect = 0;
      for (const world of Object.values(newWorlds)) {
        totalCompleted += world.completed?.length || 0;
        totalPerfect += Object.values(world.stars || {}).filter(s => s === 3).length;
      }

      return {
        ...prev,
        worlds: newWorlds,
        challenges_completed: totalCompleted,
        perfect_challenges: totalPerfect,
      };
    });
  }, [progress]);

  // Unlock all challenges for testing purposes
  const unlockAll = useCallback((challengeData) => {
    if (!challengeData) return;

    const worlds = {};
    const userCode = {};
    let totalChallenges = 0;

    // Build completed state for each topic
    for (const [topicId, challenges] of Object.entries(challengeData)) {
      const challengeIds = challenges.map(c => c.id);
      const stars = {};
      challengeIds.forEach(id => { stars[id] = 3; });

      // Save solutions - prefer explicit solution field, fallback to first alternative
      for (const challenge of challenges) {
        const key = `${topicId}:${challenge.id}`;
        if (challenge.solution) {
          // Use explicit solution field (correct answer)
          userCode[key] = challenge.solution;
        } else if (challenge.alternatives && challenge.alternatives.length > 0 && challenge.alternatives[0].code) {
          // Fallback to first alternative's code
          userCode[key] = challenge.alternatives[0].code;
        }
      }

      worlds[topicId] = {
        completion: 100,
        completed: challengeIds,
        stars: stars,
      };
      totalChallenges += challengeIds.length;
    }

    // Calculate total XP (BASE_XP + 50 bonus for 3 stars per challenge)
    const totalXp = totalChallenges * (BASE_XP + 50);

    // Get all achievement names
    const allAchievements = ACHIEVEMENTS.map(a => a.name);

    // Get all topic IDs for seen_intros
    const allTopicIds = Object.keys(challengeData);

    setProgress({
      level: calculateLevel(totalXp),
      total_xp: totalXp,
      current_xp: calculateCurrentXp(totalXp),
      challenges_completed: totalChallenges,
      perfect_challenges: totalChallenges,
      achievements: allAchievements,
      worlds: worlds,
      user_code: userCode,
      seen_intros: allTopicIds,
      created: new Date().toISOString(),
      last_saved: new Date().toISOString(),
    });
  }, []);

  const value = {
    progress,
    isLoaded,
    completeChallenge,
    saveUserCode,
    getUserCode,
    markIntroSeen,
    hasSeenIntro,
    getTopicProgress,
    getChallengeStars,
    isChallengeCompleted,
    resetProgress,
    unlockAll,
    syncWithChallenges,
    xpPerLevel: XP_PER_LEVEL,
    maxLevel: MAX_LEVEL,
    achievements: ACHIEVEMENTS,
  };

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
}

export function useProgress() {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
}
